/**
 * Hook to check if the current user has an active (non-expired) subscription.
 * Used to gate practice actions (record, compare, play) for non-paid users.
 * Reads loggedInUser from localStorage (userId, uid, email); if empty, falls back
 * to Firebase Auth current user so subscriptions are found even when localStorage
 * wasn't set (e.g. WebView, new tab). Queries by userId and email (normalized to
 * lowercase) so docs are always found regardless of case.
 *
 * Also inherits access from any account linked via parentChildLinks (parent or
 * child) that has its own active subscription, so premium unlocks for the whole
 * family once either side subscribes — regardless of which side ended up
 * recorded as "parent" vs "child" (the connect flow lets either party initiate,
 * so that role isn't a reliable signal of who actually pays).
 */
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';
import { useLinkedIdentity } from './useLinkedIdentity';

const db = getFirestore(firebaseApp);

const SUB_LOG = '[Subscriptions]';

interface SubscriptionDoc {
  id: string;
  productId: string;
  userId: string | null;
  expiresAt: Date | string | { toDate: () => Date } | { seconds: number };
}

function parseExpiresAt(expiresAt: SubscriptionDoc['expiresAt']): Date {
  if (expiresAt instanceof Date) return expiresAt;
  if (typeof expiresAt === 'string') return new Date(expiresAt);
  if (expiresAt && typeof (expiresAt as { toDate?: () => Date }).toDate === 'function') {
    return (expiresAt as { toDate: () => Date }).toDate();
  }
  if (expiresAt && typeof expiresAt === 'object' && 'seconds' in (expiresAt as object)) {
    return new Date((expiresAt as unknown as { seconds: number }).seconds * 1000);
  }
  return new Date(0);
}

export function useHasActiveSubscription(): {
  hasActiveSubscription: boolean;
  loading: boolean;
} {
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const { userId, email, linkedEmails } = useLinkedIdentity();

  useEffect(() => {
    if (!userId && !email) {
      console.log(
        SUB_LOG,
        'useHasActiveSubscription: no userId/email, setting hasActiveSubscription=false',
      );
      setLoading(false);
      setHasActiveSubscription(false);
      return;
    }
    // Wait for the linked-accounts lookup so we know the full set of sources to
    // listen to before deciding "loading" is done.
    if (linkedEmails === null) return;

    setLoading(true);
    const subsRef = collection(db, 'subscriptions');
    const docsBySource = new Map<string, SubscriptionDoc[]>();

    const sources: string[] = [];
    if (userId) sources.push('ownUserId');
    if (email) sources.push('ownEmail');
    linkedEmails.forEach((le) => sources.push(`linked:${le}`));
    const gotSnapshot = new Set<string>();

    const recompute = (): void => {
      const merged: SubscriptionDoc[] = [];
      Array.from(docsBySource.values()).forEach((docs) => {
        docs.forEach((doc) => {
          if (!merged.some((d) => d.id === doc.id)) merged.push(doc);
        });
      });
      const sorted = merged.sort((a, b) => {
        const aTime = parseExpiresAt(a.expiresAt).getTime();
        const bTime = parseExpiresAt(b.expiresAt).getTime();
        return bTime - aTime;
      });
      const doc = sorted[0];
      if (doc) {
        const expiresAt = parseExpiresAt(doc.expiresAt);
        const active = expiresAt > new Date();
        console.log(SUB_LOG, 'useHasActiveSubscription result', {
          docId: doc.id,
          productId: doc.productId,
          expiresAt: expiresAt.toISOString(),
          active,
        });
        setHasActiveSubscription(active);
      } else {
        console.log(SUB_LOG, 'useHasActiveSubscription result: no subscription doc found');
        setHasActiveSubscription(false);
      }
      if (sources.every((s) => gotSnapshot.has(s))) setLoading(false);
    };

    const unsubscribers: Array<() => void> = [];

    if (userId) {
      const qUserId = query(subsRef, where('userId', '==', userId), limit(10));
      unsubscribers.push(
        onSnapshot(
          qUserId,
          (snap) => {
            docsBySource.set(
              'ownUserId',
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add('ownUserId');
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by userId', {
              count: docsBySource.get('ownUserId')?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription userId listener error', err);
            gotSnapshot.add('ownUserId');
            recompute();
          },
        ),
      );
    }

    if (email) {
      const emailLower = email.toLowerCase();
      const qEmail = query(subsRef, where('email', '==', emailLower), limit(10));
      unsubscribers.push(
        onSnapshot(
          qEmail,
          (snap) => {
            docsBySource.set(
              'ownEmail',
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add('ownEmail');
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by email', {
              count: docsBySource.get('ownEmail')?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription email listener error', err);
            gotSnapshot.add('ownEmail');
            recompute();
          },
        ),
      );
    }

    linkedEmails.forEach((linkedEmail) => {
      const sourceKey = `linked:${linkedEmail}`;
      const qLinked = query(subsRef, where('email', '==', linkedEmail), limit(10));
      unsubscribers.push(
        onSnapshot(
          qLinked,
          (snap) => {
            docsBySource.set(
              sourceKey,
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add(sourceKey);
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by linked account email', {
              linkedEmail,
              count: docsBySource.get(sourceKey)?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription linked account listener error', err);
            gotSnapshot.add(sourceKey);
            recompute();
          },
        ),
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId, email, linkedEmails]);

  return { hasActiveSubscription, loading };
}
